import { ActivityItem } from 'app/data/types/activity';
import { formatLocaleTime, formatLocaleTimeRange } from '@hqo/date-time/locale-time';
import { createDateTimeFormat, formatRelativeTime } from '@hqo/date-time/core';

/**
 * createDateTimeFormat degrades an INVALID zone to UTC loudly, but `undefined` makes it omit
 * the key entirely and Intl then uses the DEVICE zone. buildingTimezone is reachable as
 * undefined (the mappers omit it when the building record is null), so the floor has to be
 * set here or a non-UTC reader silently gets their own day back.
 */
const UTC = 'UTC';

/** Relative-day bucket for a scheduled item's "when" line. `date` → show the formatted date. */
export type DayKey = 'today' | 'tomorrow' | 'yesterday' | 'date';

export interface WhenParts {
  dayKey: DayKey;
  /** "Mon, Jun 8" — used when `dayKey === 'date'`. */
  dateLabel: string;
  /** "2:30 PM" (building-local). */
  timeLabel: string;
}

/**
 * The calendar day `date` falls on in `timeZone`, as `YYYY-MM-DD`.
 *
 * Fixed en-CA on purpose: its numeric date format IS `YYYY-MM-DD`, so no locale's field order can
 * reach the output. createDateTimeFormat is still the constructor because it validates the zone.
 *
 * Shared with the deeplink builder so a visit's tile and the day its link opens are decided by one
 * computation — if the zone is wrong, both are wrong the same way rather than disagreeing with
 * each other.
 */
export const dayInZone = (date: Date, timeZone: string | undefined): string =>
  createDateTimeFormat('en-CA', {
    timeZone: timeZone ?? UTC,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);

/** Integer day index (days since epoch) of `date` as seen in `timeZone`. */
const dayIndexInZone = (date: Date, timeZone: string | undefined): number => {
  const [year, month, day] = dayInZone(date, timeZone).split('-').map(Number);

  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
};

/** A single local time — BUILDING's timezone, USER's locale (the RES-741 split). */
export const formatTimeLabel = (timestamp: string, timeZone: string | undefined, locale = 'en-US'): string =>
  formatLocaleTime(new Date(timestamp), locale, timeZone ?? UTC);

/**
 * A local time RANGE — formatted as a range, never two labels joined. The locale owns the separator
 * (thin-spaced in en, bare in de/pl) and collapses a day period both ends share ("4:00 – 6:00 PM").
 */
export const formatTimeRangeLabel = (
  startTimestamp: string,
  endTimestamp: string,
  timeZone: string | undefined,
  locale = 'en-US',
): string => formatLocaleTimeRange(new Date(startTimestamp), new Date(endTimestamp), locale, timeZone ?? UTC);

/**
 * Bucket a timestamp into today/tomorrow/yesterday/date in the BUILDING's timezone (not the
 * device's — the RES-741 building-vs-user trap), and format the local time. The caller maps
 * `dayKey` → a localized "Today"/"Tomorrow"/"Yesterday" label and falls back to `dateLabel`.
 */
export const formatWhen = (
  timestamp: string,
  timeZone: string | undefined,
  now: Date = new Date(),
  locale = 'en-US',
): WhenParts => {
  const date = new Date(timestamp);
  const diffDays = dayIndexInZone(date, timeZone) - dayIndexInZone(now, timeZone);

  let dayKey: DayKey = 'date';
  if (diffDays === 0) dayKey = 'today';
  else if (diffDays === 1) dayKey = 'tomorrow';
  else if (diffDays === -1) dayKey = 'yesterday';

  const dateLabel = createDateTimeFormat(locale, {
    timeZone: timeZone ?? UTC,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(date);

  const timeLabel = formatTimeLabel(timestamp, timeZone, locale);

  return { dayKey, dateLabel, timeLabel };
};

/**
 * Human "2 hours ago" / "yesterday" for the open-request footer meta. `numeric: 'auto'` is what
 * keeps the idiom rather than "1 day ago". The hand-rolled version this replaced handed Intl a bare
 * tag, which resolves to the DEVICE locale instead of throwing — invisible on an English CI box.
 */
export const fromNowLabel = (timestamp: string, now: Date = new Date(), locale = 'en'): string =>
  formatRelativeTime(timestamp, { locale, now, numeric: 'auto' });

/**
 * Compose the raw "where" subtitle from an item's per-type display fields. Returns only the
 * non-translatable pieces (venue / floor / location); the card appends localized copy like
 * "Seats {n}" and the visitor host framing.
 */
export const formatWhere = (item: ActivityItem): string => {
  const { venue, floor, location, buildingName } = item.details;
  if (location) return location;
  if (venue && floor) return `${venue} · ${floor}`;
  // Building last, and only when nothing finer exists: on a tile that already names a room and a
  // floor it adds nothing, but an event carrying none of them currently renders no `where` at all.
  return venue || floor || buildingName || '';
};
